import { useEffect, useRef } from "react";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { guardURL } from "../../../../Apis/Constants";
import { FOODIE_MEAL_ENTRY_SERVICE } from "../../../../reduxToolkit/EndPoints";
import { ACTION_MODEL } from "../../../../Helpers/ActionModel";
/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 */
export const DailyServicesWebSocket = ({
                                           item,
                                           isOnline,
                                           setIsOnline,
                                           setActiveCafeteriaOptions,
                                           setEmployees
                                       }) => {
    const subscriptionsRef = useRef([]);
    const itemRef = useRef(item);
    const clientRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const connectionTimeoutRef = useRef(null);
    const isReconnectingRef = useRef(false);
    const connectionSuccessRef = useRef(false);
    const isMountedRef = useRef(false);

    /**
     * Sync latest item into ref to avoid stale closures
     */
    useEffect(() => {
        itemRef.current = item;
    }, [item]);

    /**
     * Updates UI state with cafeteria options and employees.
     * Enriches employees with `shownAt` timestamp and persists them in localStorage.
     *
     * @param {Object} data - WebSocket payload.
     * @param {Array} [data.cafeterias] - List of cafeterias.
     * @param {Object} [data.employees] - Paginated employees object.
     * @param {Array} [data.employees.content] - Employees list.
     */
    const handleSetData = (data) => {
        setActiveCafeteriaOptions(data?.cafeterias ?? []);
        setEmployees(data?.employees);
    };

    /**
     * Publishes request with current filters
     */
    const publishDailyKitchenAccess = () => {
        const client = clientRef.current;
        if (!client?.connected || !itemRef.current) return;

        client.publish({
            destination: FOODIE_MEAL_ENTRY_SERVICE.publishDailyKitchenAccess,
            body: JSON.stringify(itemRef.current)
        });
    };

    /**
     * Fully closes client + underlying socket + timeouts
     */
    const destroyWebSocket = async () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
        }

        subscriptionsRef.current.forEach((sub) => {
            try {
                sub.unsubscribe();
            } catch (e) {}
        });
        subscriptionsRef.current = [];

        const client = clientRef.current;
        if (!client) {
            isReconnectingRef.current = false;
            connectionSuccessRef.current = false;
            return;
        }

        try {
            await client.deactivate();
        } catch (e) {}

        try {
            if (client.webSocket) client.webSocket.close();
            else if (client._webSocket) client._webSocket.close();
        } catch (e) {}

        clientRef.current = null;
        isReconnectingRef.current = false;
        connectionSuccessRef.current = false;
    };

    /**
     * Schedule a reconnect if not already pending
     */
    const scheduleReconnect = (delay = 5000) => {
        if (!isMountedRef.current) return;
        if (reconnectTimeoutRef.current || isReconnectingRef.current) return;

        isReconnectingRef.current = true;

        reconnectTimeoutRef.current = setTimeout(async () => {
            reconnectTimeoutRef.current = null;

            if (!isMountedRef.current) {
                isReconnectingRef.current = false;
                return;
            }

            await destroyWebSocket();
            connectWebSocket();
        }, delay);
    };

    /**
     * Initialize socket connection
     */
    const connectWebSocket = () => {
        if (!isMountedRef.current) return;
        if (clientRef.current?.active || clientRef.current?.connected) return;

        const client = new Client({
            webSocketFactory: () => new SockJS(guardURL),
            debug: () => {},
            reconnectDelay: 0,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,

            onConnect: () => {
                if (!isMountedRef.current) return;

                setIsOnline(true);
                connectionSuccessRef.current = true;
                isReconnectingRef.current = false;

                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                subscriptionsRef.current.forEach((sub) => {
                    try {
                        sub.unsubscribe();
                    } catch (e) {}
                });
                subscriptionsRef.current = [];

                const subscription = client.subscribe(
                    FOODIE_MEAL_ENTRY_SERVICE.topicDailyKitchenAccess,
                    (message) => {
                        let parsed;

                        try {
                            parsed = JSON.parse(message.body);
                        } catch (e) {
                            return;
                        }

                        if (parsed?.action === ACTION_MODEL.REFRESH) {
                            publishDailyKitchenAccess();
                            return;
                        }

                        if (parsed?.data) {
                            handleSetData(parsed.data);
                        }
                    }
                );

                subscriptionsRef.current = [subscription];
                publishDailyKitchenAccess();
            },

            onStompError: () => {
                if (!isMountedRef.current) return;

                setIsOnline(false);
                connectionSuccessRef.current = false;
                scheduleReconnect();
            },

            onWebSocketClose: () => {
                if (!isMountedRef.current) return;

                setIsOnline(false);
                connectionSuccessRef.current = false;
                scheduleReconnect();
            },

            onWebSocketError: () => {
                if (!isMountedRef.current) return;

                setIsOnline(false);
                connectionSuccessRef.current = false;
                scheduleReconnect();
            }
        });

        clientRef.current = client;
        client.activate();

        connectionTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) return;

            const currentClient = clientRef.current;
            if (currentClient === client && !client.connected && !connectionSuccessRef.current) {
                await destroyWebSocket();
                scheduleReconnect(4000);
            }
        }, 10000);
    };

    /**
     * Mount / unmount lifecycle
     */
    useEffect(() => {
        isMountedRef.current = true;
        connectWebSocket();

        return () => {
            isMountedRef.current = false;
            destroyWebSocket();
        };
    }, []);

    /**
     * Republish when filters change
     */
    useEffect(() => {
        if (clientRef.current?.connected && item) {
            publishDailyKitchenAccess();
        }
    }, [item]);

    /**
     * Manual reconnect
     */
    const reconnectWebSocket = async () => {
        if (isOnline || clientRef.current?.connected) return;

        await destroyWebSocket();
        connectWebSocket();
    };

    return { reconnectWebSocket };
};
